import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {handleErrorResponse} from "../utils/util"
import { USER_ROLE } from "../constants/index"
import {
  ActionDetailCreateData, ActionDetailDestroyData,
  type ActionDetailRetrieveData, type ActionDetailUpdateData,
  type ActionListData,
  ActionService
} from "api";
import i18n from 'i18next';
import { resetAppState } from "../store/common";

// Get list action
export const getListAction = createAsyncThunk(
  "getListAction",
  async (data: ActionListData, thunkAPI: any) => {
    try {
      const res = await ActionService.actionList(data) as any;
      if (res) {
        return res; 
      }
      return thunkAPI.rejectWithValue(i18n.t('MSG_GLOBAL_DATA_LOAD_FAILED'));
    } catch (error: any) {
      return handleErrorResponse(error, USER_ROLE.COMPANY, thunkAPI);
    }
  },
);

// Get list options related action
export const getListOptionsRelatedAction = createAsyncThunk(
  "getListOptionsRelatedAction",
  async (_, thunkAPI: any) => {
    try {
      const res = await ActionService.actionGetOptionsRelatedRetrieve() as any;
      if (res) { return res; }
      return thunkAPI.rejectWithValue(i18n.t('MSG_GLOBAL_DATA_LOAD_FAILED'));
    } catch (error: any) { 
      return handleErrorResponse(error, USER_ROLE.COMPANY, thunkAPI);
    }
  },
);

// Get action detail
export const getActionDetail = createAsyncThunk(
  "getActionDetail",
  async (data: ActionDetailRetrieveData, thunkAPI: any) => {
    try {
      const res = await ActionService.actionDetailRetrieve(data) as any;
      if (res.success) { return res; }
      return thunkAPI.rejectWithValue(i18n.t('MSG_GLOBAL_DATA_LOAD_FAILED'));
    } catch (error: any) {
      return handleErrorResponse(error, USER_ROLE.COMPANY, thunkAPI); 
    }
  },
);

// Add action items
export const addActionItems = createAsyncThunk(
  "addActionItems",
  async (data: ActionDetailCreateData, thunkAPI: any) => {
    try {
      const res = await ActionService.actionDetailCreate(data) as any;
      if (res) { return res; }
      return thunkAPI.rejectWithValue(i18n.t('MSG_GLOBAL_DATA_LOAD_FAILED'));
    } catch (error: any) {
      return handleErrorResponse(error, USER_ROLE.COMPANY, thunkAPI);
    }
  },
);

// Update action items
export const updateActionItems = createAsyncThunk(
  "updateActionItems",
  async (data: ActionDetailUpdateData, thunkAPI: any) => {
    try {
      const res = await ActionService.actionDetailUpdate(data) as any;
      if (res) { return res; }
      else {
        return thunkAPI.rejectWithValue(i18n.t('MSG_GLOBAL_DATA_LOAD_FAILED'));
      }
    } catch (error: any) {
      return handleErrorResponse(error, USER_ROLE.COMPANY, thunkAPI);
    }
  },
);

// Delete action item
export const deleteActionItem = createAsyncThunk(
  "deleteActionItem",
  async (data: ActionDetailDestroyData, thunkAPI: any) => {
    try {
      const res = await ActionService.actionDetailDestroy(data) as any;
      if (res) { return res; }
      return thunkAPI.rejectWithValue(i18n.t('MSG_GLOBAL_DATA_LOAD_FAILED'));
    } catch (error: any) {
      return handleErrorResponse(error, USER_ROLE.COMPANY, thunkAPI);
    }
  },
);

// State ban đầu của Redux
interface loadingState {
  loading: boolean;
  loadingSearchAction: boolean;
  loadingGetActionItem: boolean;
  error: string | null;
  actionStatus: string;
  actionName: string;
  actionList: any,
  pageSize: number,
  optionsRelatedAction: any;
  actionSelected: any;
  actionItems: any;
  currentPage: number,
  globalConditionSearch: any;
}
const initialState: loadingState = {
  loading: false,
  loadingSearchAction: false,
  loadingGetActionItem: false,
  error: null,
  actionStatus: "idle",
  actionName: "",
  isSuccess: true,
  apiMessage: "",
  actionList: {},
  pageSize: 10,
  optionsRelatedAction: { action_status: [], staffs: [] },
  actionSelected: {},
  actionItems: [],
  currentPage: 1,
  globalConditionSearch: {
    searchTalentQuery: "",
    searchStaffQuery: "",
    searchJobQuery: "",
    actionStatus: null,
    sortBy: ""
  }
};

// Tạo slice với các reducers và extraReducers (cho async action)
export const loadingSlice = createSlice({
  name: "loadingSlice",
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setLoadingSearchAction: (state, action) => {
      state.loadingSearchAction = action.payload;
    },
    setLoadingGetActionItem: (state, action) => {
      state.loadingGetActionItem = action.payload;
    },
    setActionSelected: (state, action) => {
      state.actionSelected = action.payload;
    },
    setActionName: (state, action) => {
      state.actionName = action.payload;
    },
    setActionStatus: (state, action) => {
      state.actionStatus = action.payload;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setGlobalConditionSearch: (state, action) => {
      state.globalConditionSearch = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // reset state
      .addCase(resetAppState, () => initialState)
      
      // Get list chat staff
      .addCase(getListAction.pending, (state) => {
        state.loadingSearchAction = true;
        state.loading = true;
        state.error = null;
        state.actionStatus = "pending";
      })
      .addCase(getListAction.fulfilled, (state, action) => {
        state.loading = false;
        state.loadingSearchAction = false;
        state.actionName = "getListAction";
        state.actionStatus = "fulfilled";
        state.actionList = action.payload;
      })
      .addCase(getListAction.rejected, (state, action) => {
        state.loading = false;
        state.loadingSearchAction = false;
        state.error = action.payload as string;
        state.actionStatus = "rejected";
      })

      // Get list options related action
      .addCase(getListOptionsRelatedAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getListOptionsRelatedAction.fulfilled, (state, action) => {
        state.loading = false;
        state.optionsRelatedAction = action.payload?.data;
      })
      .addCase(getListOptionsRelatedAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Get action detail
      .addCase(getActionDetail.pending, (state) => {
        state.loading = true;
        state.loadingGetActionItem = true;
        state.error = null;
        state.actionStatus = "pending";
        state.actionName = "getActionDetail";
      })
      .addCase(getActionDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.loadingGetActionItem = false;
        state.actionStatus = "fulfilled";
        state.actionSelected = action.payload?.data?.action || {};
        state.actionItems = action.payload?.data?.action_items || [];
      })
      .addCase(getActionDetail.rejected, (state, action) => {
        state.loading = false;
        state.loadingGetActionItem = false;
        state.error = action.payload as string;
        state.actionStatus = "rejected";
      })

      // Add action items
      .addCase(addActionItems.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.actionStatus = "pending";
      })
      .addCase(addActionItems.fulfilled, (state, action) => {
        state.loading = false;
        state.actionName = "addActionItems";
        state.actionStatus = "fulfilled";
        state.isSuccess = action.payload.success;
        state.apiMessage = action.payload.message;
      })
      .addCase(addActionItems.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.actionStatus = "rejected";
      })

      // Update action items
      .addCase(updateActionItems.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.actionStatus = "pending";
      })
      .addCase(updateActionItems.fulfilled, (state, action) => {
        state.loading = false;
        state.actionName = "updateActionItems";
        state.actionStatus = "fulfilled";
        state.isSuccess = action.payload.success;
        state.apiMessage = action.payload.message;
      })
      .addCase(updateActionItems.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.actionStatus = "rejected";
      })

      // Add action items
      .addCase(deleteActionItem.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.actionStatus = "pending";
      })
      .addCase(deleteActionItem.fulfilled, (state, action) => {
        state.loading = false;
        state.actionName = "deleteActionItem";
        state.actionStatus = "fulfilled";
        state.isSuccess = action.payload.success;
        state.apiMessage = action.payload.message;
      })
      .addCase(deleteActionItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.actionStatus = "rejected";
      });
  },
});

export const {
  setLoading, setLoadingSearchAction,
  setActionSelected, setLoadingGetActionItem,
  setActionName, setActionStatus, setCurrentPage,
  setGlobalConditionSearch,
} = loadingSlice.actions;
export default loadingSlice.reducer;
